import { useState, useEffect } from 'react';
import { Modal, Table, Tag, Space, Typography, Pagination, Spin, Alert } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Text } = Typography;

interface Order {
  id: string;
  out_trade_no: string;
  sub_id?: string;
  sku_id: string;
  spu_key: string;
  purchase_type: 'renew' | 'expand';
  amount: number;
  currency: string;
  capacity_mb: number;
  status: 'pending' | 'paid' | 'cancelled';
  start_date?: string;
  end_date?: string;
  paid_at?: string;
  created_at: string;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface OrderHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  colors: {
    primary: string;
    text: string;
    textMuted: string;
    border: string;
    surface: string;
  };
}

export default function OrderHistoryModal({ visible, onClose, colors }: OrderHistoryModalProps) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0
  });

  const fetchOrders = async (page: number = 1, pageSize: number = 10) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/api/v1/orders/list?page=${page}&pageSize=${pageSize}`) as unknown as { 
        success: boolean; 
        data?: { orders: Order[]; pagination: PaginationInfo };
        error?: string;
      };
      
      if (response.success && response.data) {
        setOrders(response.data.orders);
        setPagination(response.data.pagination);
      } else {
        setError(response.error || '获取订单失败');
      }
    } catch (err) {
      setError('获取订单失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchOrders(1, 10);
    }
  }, [visible]);

  const handlePageChange = (page: number, pageSize?: number) => {
    fetchOrders(page, pageSize || 10);
  };

  const formatPrice = (amount: number, currency: string) => {
    const symbol = currency === 'USD' ? '$' : currency;
    return `${symbol}${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  const columns = [
    {
      title: '订单号',
      dataIndex: 'out_trade_no',
      key: 'out_trade_no',
      render: (text: string) => <Text style={{ fontFamily: 'monospace' }}>{text}</Text>,
    },
    {
      title: '套餐',
      dataIndex: 'spu_key',
      key: 'spu_key',
      render: (text: string, record: Order) => (
        <Space>
          <Text>{text === '1gb' ? '弹性扩容包' : text === '5gb' ? '超值畅存包' : text}</Text>
          <Tag color={colors.primary}>{(record.capacity_mb / 1024).toFixed(0)}GB</Tag>
        </Space>
      ),
    },
    {
      title: '类型',
      dataIndex: 'purchase_type',
      key: 'purchase_type',
      render: (text: 'renew' | 'expand') => (
        <Tag color={text === 'renew' ? 'blue' : 'green'}>
          {text === 'renew' ? '续费' : '实时扩充'}
        </Tag>
      ),
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number, record: Order) => (
        <Text strong style={{ color: colors.primary }}>
          {formatPrice(amount, record.currency)}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusMap: Record<string, { color: string; text: string }> = {
          pending: { color: 'orange', text: '待支付' },
          paid: { color: 'green', text: '已支付' },
          cancelled: { color: 'red', text: '已取消' },
        };
        const { color, text } = statusMap[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '有效期',
      key: 'validity',
      render: (_: any, record: Order) => (
        <Space direction="vertical" size={0}>
          {record.start_date && (
            <Text style={{ fontSize: 12 }}>
              开始: {record.start_date}
            </Text>
          )}
          {record.end_date && (
            <Text style={{ fontSize: 12 }}>
              结束: {record.end_date}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '支付时间',
      dataIndex: 'paid_at',
      key: 'paid_at',
      render: (paid_at?: string) => formatDate(paid_at),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: string) => formatDate(created_at),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <HistoryOutlined style={{ color: colors.primary }} />
          <span style={{ color: colors.text }}>付费记录</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      maskClosable={false}
      styles={{
        body: { 
          background: colors.surface,
          maxHeight: '70vh',
          overflow: 'auto'
        }
      }}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: colors.textMuted }}>加载订单记录...</p>
        </div>
      ) : error ? (
        <Alert
          message={error}
          type="error"
          showIcon
          action={
            <button onClick={() => fetchOrders(pagination.page, pagination.pageSize)}>
              重试
            </button>
          }
        />
      ) : (
        <>
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            pagination={false}
            scroll={{ x: 'max-content' }}
            size="middle"
          />
          <div style={{ marginTop: 16, textAlign: 'right' }}>
            <Pagination
              current={pagination.page}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={handlePageChange}
              showSizeChanger
              showTotal={(total) => `共 ${total} 条记录`}
            />
          </div>
        </>
      )}
    </Modal>
  );
}
